/** @format */

import type { FirebaseApp } from 'firebase/app'
import type { Messaging, MessagePayload } from 'firebase/messaging'
import type { Analytics } from 'firebase/analytics'
import type { FirebaseStorage } from 'firebase/storage'

// Firebase config - lazy initialization to avoid blocking app startup
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
}

// Lazy-initialized singletons
let app: FirebaseApp | null = null
let messaging: Messaging | null = null
let messagingPromise: Promise<Messaging | null> | null = null
let analytics: Analytics | null = null
let storage: FirebaseStorage | null = null
let appInitialized = false

/**
 * Lazily initialize the Firebase app and core services.
 * Called on first use instead of at module load time.
 */
const ensureApp = async () => {
  if (appInitialized) return app
  const { initializeApp } = await import('firebase/app')
  app = initializeApp(firebaseConfig)
  appInitialized = true
  return app
}

// Google Analytics disabled at runtime — see logAnalyticsEvent / initializeFirebase below.
// To re-enable, restore the previous getAnalytics(firebaseApp) initialization here.
const ensureAnalytics = async (): Promise<Analytics | null> => null

const ensureStorage = async () => {
  if (storage) return storage
  const firebaseApp = await ensureApp()
  const { getStorage } = await import('firebase/storage')
  storage = getStorage(firebaseApp!)
  return storage
}

/**
 * Initialize Firebase Messaging if supported
 */
const initializeMessaging = async () => {
  try {
    const { getMessaging, isSupported } = await import('firebase/messaging')
    const isSupportedBrowser = await isSupported()
    if (isSupportedBrowser) {
      const firebaseApp = await ensureApp()
      messaging = getMessaging(firebaseApp!)
      return messaging
    }
    return null
  } catch {
    return null
  }
}

/**
 * Get messaging instance (initialize if needed)
 */
const getMessagingInstance = () => {
  if (!messagingPromise) {
    messagingPromise = initializeMessaging()
  }
  return messagingPromise
}

/**
 * Get Firebase messaging token
 */
export const getMessagingToken = async (vapidKey?: string) => {
  const messagingInstance = await getMessagingInstance()
  if (!messagingInstance) return null

  try {
    const { getToken } = await import('firebase/messaging')
    const registration = await navigator.serviceWorker.ready
    const vapid = vapidKey || import.meta.env.VITE_FIREBASE_VAPID_KEY
    return await getToken(messagingInstance, {
      vapidKey: vapid,
      serviceWorkerRegistration: registration,
    })
  } catch {
    return null
  }
}

/**
 * Delete the current Firebase messaging token
 */
export const deleteMessagingToken = async () => {
  const messagingInstance = await getMessagingInstance()
  if (!messagingInstance) return false

  try {
    const { deleteToken } = await import('firebase/messaging')
    await deleteToken(messagingInstance)
    return true
  } catch {
    return false
  }
}

/**
 * Setup a callback for foreground messages
 */
export const setupOnMessage = async (callback: (payload: MessagePayload) => void) => {
  const messagingInstance = await getMessagingInstance()
  if (!messagingInstance) return () => {}

  const { onMessage } = await import('firebase/messaging')
  return onMessage(messagingInstance, callback)
}

/**
 * Request notification permission
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    return 'unsupported'
  }

  try {
    return await Notification.requestPermission()
  } catch {
    return 'error'
  }
}

/**
 * Log an analytics event
 */
export const logAnalyticsEvent = async (eventName: string, eventParams: Record<string, string | number | boolean> = {}) => {
  if (import.meta.env.DEV) return
  const analyticsInstance = await ensureAnalytics()
  if (analyticsInstance) {
    try {
      const { logEvent } = await import('firebase/analytics')
      logEvent(analyticsInstance, eventName, eventParams)
    } catch {
      // Silently fail
    }
  }
}

/**
 * Initialize Firebase services with error handling
 */
export const initializeFirebase = async () => {
  try {
    await initializeMessaging()
    return { success: true }
  } catch {
    return { success: false }
  }
}

/**
 * Get the Firebase storage instance (lazy-loaded)
 */
export const getFirebaseStorage = () => ensureStorage()

export { initializeMessaging, app, storage }
